import jwt from 'jsonwebtoken';
import axios from 'axios';
import { REST_API_URI } from '@/index';
import LocalStorage from '@/service/LocalStorage';
import { setAccessToken } from '@/utils/accessToken';
import ApiService from '@/service/Api';

interface TokenInfo {
  isValid: boolean;
  timeUntilExpiry: number;
}

const getTokenInfo = (token: string | null | undefined): TokenInfo => {
  if (!token) {
    return { isValid: false, timeUntilExpiry: 0 };
  }

  const decodedToken = jwt.decode(token) as {
    exp: number;
  };

  if (!decodedToken || !decodedToken.exp) {
    return { isValid: false, timeUntilExpiry: 0 };
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = decodedToken.exp - currentTime;

  return { isValid: true, timeUntilExpiry };
};

export const isTokenExpired = (token: string | null | undefined): boolean => {
  const { isValid, timeUntilExpiry } = getTokenInfo(token);
  return !isValid || timeUntilExpiry <= 0;
};

export const validateToken = async (token: string | null | undefined, refresh = true) => {
  const { isValid, timeUntilExpiry } = getTokenInfo(token);

  if (!isValid) {
    return false;
  }

  if (!refresh) {
    return timeUntilExpiry > 0;
  }

  // expired or will expire in less than 10 minutes
  if (timeUntilExpiry <= 600) {
    try {
      const response = await axios.post(
        `${REST_API_URI}/services/auth/refresh_token`,
        {},
        {
          headers: {
            'x-refresh-token': `${LocalStorage.get('refresh_token')}`,
          },
        }
      );
      if (response.data.authorized && response.data.accessToken) {
        setAccessToken(response.data.accessToken);
        ApiService.init(response.data.accessToken);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      if (timeUntilExpiry <= 0) {
        return false;
      }

      console.error('Failed to refresh token', error);
      return true;
    }
  }

  return true;
};
